import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import { escapeXml, formatChapterLabel, splitForDropCap } from '@/lib/exportFormat';

// Color themes for book export (legacy - kept for backward compatibility)
export const BOOK_THEMES = {
  classic: {
    name: 'Classic Elegance',
    primary: '#2D2D2D',
    secondary: '#8B7355',
    accent: '#D4AF37',
    background: '#FFFFF0',
    text: '#1A1A1A'
  },
  vintage: {
    name: 'Warm Vintage',
    primary: '#5C4033',
    secondary: '#A0826D',
    accent: '#CD853F',
    background: '#FFF8DC',
    text: '#3E2723'
  },
  modern: {
    name: 'Modern Minimalist',
    primary: '#000000',
    secondary: '#666666',
    accent: '#4A90E2',
    background: '#FFFFFF',
    text: '#212121'
  },
  royal: {
    name: 'Royal Purple',
    primary: '#4A148C',
    secondary: '#7B1FA2',
    accent: '#BA68C8',
    background: '#F3E5F5',
    text: '#311B92'
  },
  forest: {
    name: 'Forest Green',
    primary: '#1B5E20',
    secondary: '#388E3C',
    accent: '#81C784',
    background: '#E8F5E9',
    text: '#1B5E20'
  },
  ai: {
    name: 'AI-Designed',
    primary: '#2D2D2D',
    secondary: '#666666',
    accent: '#8B7355',
    background: '#FFFEF9',
    text: '#1A1A1A'
  }
};

/**
 * Default AI format guide for fallback
 */
const DEFAULT_FORMAT_GUIDE = {
  typography: {
    headingFont: "Times New Roman",
    bodyFont: "Times New Roman",
    titleSize: 26,
    chapterTitleSize: 20,
    bodySize: 11,
    lineHeight: 1.6
  },
  layout: {
    marginTop: 25,
    marginBottom: 25,
    marginLeft: 22,
    marginRight: 22,
    paragraphIndent: 15,
    paragraphSpacing: 6,
    textAlign: "justify"
  },
  chapterDesign: {
    useDropCap: true,
    dropCapLines: 2,
    chapterNumberStyle: "elegant",
    chapterStartNewPage: true,
    decorativeElement: "simple line",
    chapterOpeningSpacing: 50
  },
  specialElements: {
    pageNumberStyle: "bottom center",
    headerStyle: "book title",
    sectionBreaks: "three dots"
  },
  colorScheme: {
    primary: "#2D2D2D",
    secondary: "#666666",
    accent: "#8B7355",
    background: "#FFFEF9",
    text: "#1A1A1A"
  }
};

/**
 * Helper to convert hex color to RGB array
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
}

/**
 * Draw decorative element based on style
 */
function drawDecorativeElement(doc, style, x, y, width, colors) {
  const rgb = hexToRgb(colors.accent || colors.secondary);
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  
  switch (style) {
    case 'flourish':
      // Elegant curved lines
      doc.setLineWidth(0.5);
      doc.line(x + width * 0.3, y, x + width * 0.7, y);
      doc.setLineWidth(0.3);
      doc.line(x + width * 0.35, y - 2, x + width * 0.65, y - 2);
      doc.line(x + width * 0.35, y + 2, x + width * 0.65, y + 2);
      break;
    case 'thick line':
      doc.setLineWidth(2);
      doc.line(x + width * 0.2, y, x + width * 0.8, y);
      break;
    case 'stars':
      doc.setFontSize(12);
      doc.text('✦  ✦  ✦', x + width / 2, y, { align: 'center' });
      break;
    case 'dots':
      doc.setFontSize(14);
      doc.text('• • •', x + width / 2, y, { align: 'center' });
      break;
    case 'simple line':
    default:
      doc.setLineWidth(0.5);
      doc.line(x + width * 0.25, y, x + width * 0.75, y);
      break;
  }
  doc.setLineWidth(0.5);
}

/**
 * Helper function to create a classic text-only cover page
 */
function createClassicCover(doc, book, authorName, theme, pageWidth, pageHeight, maxWidth) {
  // Background color
  doc.setFillColor(theme.background);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Decorative border
  doc.setDrawColor(theme.primary);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
  doc.setLineWidth(0.5);
  doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

  // Book Title
  doc.setTextColor(theme.primary);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(book.title, maxWidth - 40);
  let yPos = 80;
  titleLines.forEach(line => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;
  });

  // Genre
  yPos += 20;
  doc.setFontSize(14);
  doc.setTextColor(theme.secondary);
  doc.setFont('helvetica', 'italic');
  doc.text(book.genre.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });

  // Author
  yPos = pageHeight - 60;
  doc.setFontSize(18);
  doc.setTextColor(theme.primary);
  doc.setFont('helvetica', 'normal');
  doc.text('By', pageWidth / 2, yPos, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.text(authorName || 'Anonymous Author', pageWidth / 2, yPos + 10, { align: 'center' });
}

/**
 * Helper: Create professional text-only cover
 */
function createProfessionalTextCover(doc, book, authorName, colors, typography, pageWidth, pageHeight, maxWidth) {
  const primaryRgb = hexToRgb(colors.primary);
  const secondaryRgb = hexToRgb(colors.secondary);
  const accentRgb = hexToRgb(colors.accent);
  const bgRgb = hexToRgb(colors.background);
  
  // Background
  doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Elegant double border
  doc.setDrawColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.setLineWidth(2);
  doc.rect(12, 12, pageWidth - 24, pageHeight - 24);
  doc.setLineWidth(0.5);
  doc.rect(16, 16, pageWidth - 32, pageHeight - 32);
  
  // Title
  let yPos = 85;
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.setFontSize(typography.titleSize || 28);
  doc.setFont('times', 'bold');
  const titleLines = doc.splitTextToSize(book.title, maxWidth - 40);
  titleLines.forEach(line => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 14;
  });
  
  // Decorative line
  yPos += 15;
  doc.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.setLineWidth(1);
  doc.line(pageWidth * 0.3, yPos, pageWidth * 0.7, yPos);
  
  // Genre
  yPos += 20;
  doc.setTextColor(secondaryRgb[0], secondaryRgb[1], secondaryRgb[2]);
  doc.setFontSize(13);
  doc.setFont('times', 'italic');
  doc.text(book.genre.charAt(0).toUpperCase() + book.genre.slice(1), pageWidth / 2, yPos, { align: 'center' });
  
  // Author at bottom
  yPos = pageHeight - 55;
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.setFontSize(12);
  doc.setFont('times', 'normal');
  doc.text('by', pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;
  doc.setFontSize(16);
  doc.setFont('times', 'bold');
  doc.text(authorName || 'Anonymous Author', pageWidth / 2, yPos, { align: 'center' });
}

/**
 * Render the whole book into a jsPDF document.
 *
 * Called twice. The first pass exists only to learn which page each chapter
 * actually starts on; the second uses those numbers in the table of contents.
 * Nothing cheaper can produce a correct TOC — the previous code guessed with
 * `currentPage + index + 2`, which assumes every chapter is exactly one page,
 * so in a real book every entry pointed at the wrong page.
 *
 * The TOC's own length does not depend on the numbers printed in it, so
 * pagination is identical across both passes.
 */
function renderProfessionalBook(doc, book, chapters, authorName, coverImageUrl, guide, chapterPages) {
  const colors = guide.colorScheme || DEFAULT_FORMAT_GUIDE.colorScheme;
  const typography = guide.typography || DEFAULT_FORMAT_GUIDE.typography;
  const layout = guide.layout || DEFAULT_FORMAT_GUIDE.layout;
  const chapterDesign = guide.chapterDesign || DEFAULT_FORMAT_GUIDE.chapterDesign;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const marginTop = layout.marginTop || 25;
  const marginBottom = layout.marginBottom || 25;
  const marginLeft = layout.marginLeft || 22;
  const marginRight = layout.marginRight || 22;
  const maxWidth = pageWidth - marginLeft - marginRight;

  const bodySize = typography.bodySize || 11;
  const lineHeight = typography.lineHeight || 1.6;
  // jsPDF works in mm; 0.3528 converts points to mm.
  const lineAdvance = bodySize * lineHeight * 0.3528;
  const paragraphSpacing = layout.paragraphSpacing || 6;
  const paragraphIndent = layout.paragraphIndent || 15;

  const primaryRgb = hexToRgb(colors.primary);
  const secondaryRgb = hexToRgb(colors.secondary);
  const textRgb = hexToRgb(colors.text);
  const bgRgb = hexToRgb(colors.background);

  let yPosition = marginTop;
  let currentPage = 1;
  const recordedPages = new Map();

  const paintBackground = () => {
    doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
  };

  // Page numbers, which the shipped exporter omitted entirely. Front matter
  // stays unnumbered, as in print.
  const addPageFooter = () => {
    if (currentPage <= 2) return;
    doc.setFontSize(9);
    doc.setFont('times', 'normal');
    doc.setTextColor(secondaryRgb[0], secondaryRgb[1], secondaryRgb[2]);
    doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  };

  const addNewPage = () => {
    addPageFooter();
    doc.addPage();
    currentPage++;
    yPosition = marginTop;
    paintBackground();
  };

  const checkPageBreak = (needed = 10) => {
    if (yPosition + needed > pageHeight - marginBottom) {
      addNewPage();
      return true;
    }
    return false;
  };

  const setBodyStyle = () => {
    doc.setFontSize(bodySize);
    doc.setFont('times', 'normal');
    doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
  };

  // === COVER ===
  paintBackground();
  if (coverImageUrl) {
    try {
      const imageType = coverImageUrl.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(coverImageUrl, imageType, 0, 0, pageWidth, pageHeight);
      doc.setFillColor(0, 0, 0);
      doc.rect(0, pageHeight - 75, pageWidth, 75, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(typography.titleSize || 26);
      doc.setFont('times', 'bold');
      yPosition = pageHeight - 60;
      doc.splitTextToSize(book.title, maxWidth).forEach(line => {
        doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 12;
      });
      doc.setFontSize(14);
      doc.setFont('times', 'italic');
      doc.text(`by ${authorName || 'Anonymous Author'}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    } catch (error) {
      createProfessionalTextCover(doc, book, authorName, colors, typography, pageWidth, pageHeight, maxWidth);
    }
  } else {
    createProfessionalTextCover(doc, book, authorName, colors, typography, pageWidth, pageHeight, maxWidth);
  }

  // === TITLE PAGE ===
  addNewPage();
  yPosition = 70;
  doc.setFontSize(typography.titleSize || 26);
  doc.setFont('times', 'bold');
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.splitTextToSize(book.title, maxWidth - 20).forEach(line => {
    doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 14;
  });
  yPosition += 10;
  drawDecorativeElement(doc, chapterDesign.decorativeElement, marginLeft, yPosition, maxWidth, colors);
  yPosition = pageHeight - 60;
  doc.setFontSize(14);
  doc.setFont('times', 'normal');
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.text('by', pageWidth / 2, yPosition, { align: 'center' });
  doc.setFontSize(18);
  doc.setFont('times', 'bold');
  doc.text(authorName || 'Anonymous Author', pageWidth / 2, yPosition + 12, { align: 'center' });

  const sortedChapters = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);

  // === CONTENTS ===
  addNewPage();
  yPosition = marginTop + 15;
  doc.setFontSize(typography.chapterTitleSize || 20);
  doc.setFont('times', 'bold');
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.text('Contents', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 20;

  sortedChapters.forEach((chapter) => {
    checkPageBreak(12);
    setBodyStyle();

    doc.setFont('times', 'italic');
    doc.text(`Chapter ${chapter.chapter_number}`, marginLeft, yPosition);

    doc.setFont('times', 'normal');
    const titleX = marginLeft + 32;
    const pageLabel = chapterPages ? `${chapterPages.get(chapter.chapter_number) ?? ''}` : '';
    // Reserve the same width on both passes so the layout cannot shift.
    const reserved = doc.getTextWidth('000');
    const titleSpace = pageWidth - marginRight - reserved - 8 - titleX;

    const fullTitle = chapter.title || '';
    let entryTitle = fullTitle;
    while (entryTitle.length > 1 && doc.getTextWidth(entryTitle) > titleSpace) {
      entryTitle = entryTitle.slice(0, -2);
    }
    if (entryTitle !== fullTitle) entryTitle = `${entryTitle}…`;
    doc.text(entryTitle, titleX, yPosition);

    if (pageLabel) {
      const dotsStart = titleX + doc.getTextWidth(entryTitle) + 3;
      const dotsEnd = pageWidth - marginRight - doc.getTextWidth(pageLabel) - 3;
      if (dotsEnd > dotsStart) {
        doc.setLineDash([1, 2]);
        doc.setDrawColor(secondaryRgb[0], secondaryRgb[1], secondaryRgb[2]);
        doc.line(dotsStart, yPosition - 1, dotsEnd, yPosition - 1);
        doc.setLineDash([]);
      }
      doc.text(pageLabel, pageWidth - marginRight, yPosition, { align: 'right' });
    }
    yPosition += 10;
  });

  // === CHAPTERS ===
  sortedChapters.forEach((chapter) => {
    addNewPage();
    recordedPages.set(chapter.chapter_number, currentPage);

    yPosition = chapterDesign.chapterOpeningSpacing || 50;

    // Honour the format guide's chapter numbering. The shipped exporter always
    // printed "Chapter N", so every style the AI could pick did nothing.
    doc.setFontSize(12);
    doc.setFont('times', 'italic');
    doc.setTextColor(secondaryRgb[0], secondaryRgb[1], secondaryRgb[2]);
    doc.text(
      formatChapterLabel(chapter.chapter_number, chapterDesign.chapterNumberStyle),
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );

    // Wrap the title — it was drawn unwrapped, so a long AI-generated title ran
    // straight off both edges of the page.
    yPosition += 12;
    doc.setFontSize(typography.chapterTitleSize || 20);
    doc.setFont('times', 'bold');
    doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    doc.splitTextToSize(chapter.title || '', maxWidth - 30).forEach(line => {
      doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 9;
    });

    yPosition += 6;
    drawDecorativeElement(doc, chapterDesign.decorativeElement, marginLeft, yPosition, maxWidth, colors);
    yPosition += 20;

    const content = chapter.polished_content || chapter.content || '';
    const paragraphs = content.split('\n\n').map(p => p.trim()).filter(Boolean);

    paragraphs.forEach((paragraph, paraIndex) => {
      const isFirst = paraIndex === 0;
      let lines;
      let capWidth = 0;
      let capLines = 0;

      if (isFirst && chapterDesign.useDropCap && paragraph.length > 1) {
        const dropCapLines = chapterDesign.dropCapLines || 2;
        const capSize = bodySize * dropCapLines * 1.2;

        doc.setFontSize(capSize);
        doc.setFont('times', 'bold');
        doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
        const capLetter = paragraph.charAt(0).toUpperCase();
        doc.text(capLetter, marginLeft, yPosition + capSize * 0.55);
        capWidth = doc.getTextWidth(capLetter) + 3;

        setBodyStyle();
        const split = splitForDropCap(
          paragraph,
          dropCapLines,
          (text, width) => doc.splitTextToSize(text, width),
          maxWidth - capWidth,
          maxWidth
        );
        lines = split.lines;
        capLines = split.capLineCount;
      } else {
        setBodyStyle();
        lines = doc.splitTextToSize(paragraph, maxWidth - (isFirst ? 0 : paragraphIndent));
      }

      setBodyStyle();
      lines.forEach((line, lineIndex) => {
        // The drop-cap paragraph previously had no page-break check at all, so a
        // long opening paragraph was drawn off the bottom of the page and lost.
        if (checkPageBreak(lineAdvance)) {
          capLines = 0; // the cap is on the previous page; stop indenting
          setBodyStyle();
        }
        let xPos = marginLeft;
        if (lineIndex < capLines) xPos = marginLeft + capWidth;
        else if (!isFirst && lineIndex === 0) xPos = marginLeft + paragraphIndent;
        doc.text(line, xPos, yPosition);
        yPosition += lineAdvance;
      });
      yPosition += paragraphSpacing;
    });
  });

  // === ABOUT THE AUTHOR ===
  addNewPage();
  yPosition = 60;
  doc.setFontSize((typography.chapterTitleSize || 20) - 2);
  doc.setFont('times', 'italic');
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.text('About the Author', pageWidth / 2, yPosition, { align: 'center' });

  yPosition += 8;
  drawDecorativeElement(doc, chapterDesign.decorativeElement, marginLeft, yPosition, maxWidth, colors);

  yPosition += 25;
  doc.setFontSize(14);
  doc.setFont('times', 'bold');
  doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
  doc.text(authorName || 'Anonymous Author', pageWidth / 2, yPosition, { align: 'center' });

  if (book.description) {
    yPosition += 20;
    setBodyStyle();
    doc.splitTextToSize(book.description, maxWidth - 30).forEach(line => {
      if (checkPageBreak(lineAdvance)) setBodyStyle();
      doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += lineAdvance;
    });
  }

  addPageFooter();
  return recordedPages;
}

/**
 * Generate the professional PDF and return base64 content.
 */
export async function generateProfessionalPDFBase64(book, chapters, authorName = '', coverImageUrl = null, formatGuide = null) {
  const guide = formatGuide || DEFAULT_FORMAT_GUIDE;

  // Pass 1 only measures. Its document goes out of scope before pass 2 allocates,
  // so one book is held at a time — this runs on the reader's phone.
  const chapterPages = renderProfessionalBook(
    new jsPDF(), book, chapters, authorName, coverImageUrl, guide, null
  );

  const doc = new jsPDF();
  renderProfessionalBook(doc, book, chapters, authorName, coverImageUrl, guide, chapterPages);

  const base64 = doc.output('datauristring').split(',')[1];
  const fileName = `${book.title.replace(/[^a-z0-9]/gi, '_')}_Professional.pdf`;
  return { base64, fileName };
}


/**
 * Generate PDF and return base64 content (for storage)
 */
export async function generatePDFBase64(book, chapters, theme = 'classic', authorName = '', coverImageUrl = null) {
  const selectedTheme = BOOK_THEMES[theme] || BOOK_THEMES.classic;
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  
  let yPosition = margin;
  let currentPage = 1;

  // Page numbers. This exporter had none at all — the cover is page 1 and stays
  // unnumbered, as in print.
  const addPageFooter = () => {
    if (currentPage <= 1) return;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(selectedTheme.secondary || selectedTheme.text);
    doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  };

  const addNewPage = () => {
    addPageFooter();
    doc.addPage();
    currentPage++;
    yPosition = margin;
  };

  // Helper function to check if new page is needed
  const checkPageBreak = (additionalHeight = 10) => {
    if (yPosition + additionalHeight > pageHeight - margin) {
      addNewPage();
      return true;
    }
    return false;
  };

  // === COVER PAGE ===
  if (coverImageUrl) {
    try {
      const imageType = coverImageUrl.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(coverImageUrl, imageType, 0, 0, pageWidth, pageHeight);
      
      doc.setFillColor(0, 0, 0);
      doc.rect(0, pageHeight - 70, pageWidth, 70, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(book.title, maxWidth - 20);
      yPosition = pageHeight - 55;
      titleLines.forEach(line => {
        doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
      });
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`by ${authorName || 'Anonymous Author'}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
    } catch (error) {
      createClassicCover(doc, book, authorName, selectedTheme, pageWidth, pageHeight, maxWidth);
    }
  } else {
    createClassicCover(doc, book, authorName, selectedTheme, pageWidth, pageHeight, maxWidth);
  }

  // Add chapters (same logic as generatePDF)
  for (const chapter of chapters) {
    addNewPage();
    
    // Chapter header
    doc.setFillColor(selectedTheme.primary);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(selectedTheme.background);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`CHAPTER ${chapter.chapter_number}`, pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const chapterTitleLines = doc.splitTextToSize(chapter.title, maxWidth - 20);
    let titleY = 30;
    chapterTitleLines.forEach(line => {
      doc.text(line, pageWidth / 2, titleY, { align: 'center' });
      titleY += 7;
    });
    
    yPosition = 60;
    
    // Content
    const content = chapter.polished_content || chapter.content || '';
    if (content) {
      doc.setTextColor(selectedTheme.text);
      doc.setFontSize(11);
      doc.setFont('times', 'normal');
      
      const paragraphs = content.split('\n\n').filter(p => p.trim());
      
      for (const paragraph of paragraphs) {
        const lines = doc.splitTextToSize(paragraph.trim(), maxWidth);
        
        for (const line of lines) {
          checkPageBreak(8);
          doc.text(line, margin, yPosition);
          yPosition += 6;
        }
        
        yPosition += 4;
      }
    }
  }

  addPageFooter();

  // No doc.save() here: the modal renders a success screen with its own download
  // button, so saving here fired a second, duplicate download of the same file.
  const fileName = `${book.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
  const base64 = doc.output('datauristring').split(',')[1];
  return { fileName, base64 };
}

/**
 * Generate Word document and return base64 content (for storage)
 */
export async function generateWordDocBase64(book, chapters, theme = 'classic', authorName = '') {
  const selectedTheme = BOOK_THEMES[theme] || BOOK_THEMES.classic;
  
  const children = [];

  // Cover page
  children.push(
    new Paragraph({ text: book.title, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER, spacing: { before: 2000, after: 400 } }),
    new Paragraph({ text: book.genre.toUpperCase(), alignment: AlignmentType.CENTER, spacing: { after: 1000 }, italics: true }),
    new Paragraph({ text: '', spacing: { after: 2000 } }),
    new Paragraph({ text: 'By', alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
    new Paragraph({ text: authorName || 'Anonymous Author', alignment: AlignmentType.CENTER, bold: true, spacing: { after: 400 } }),
    new Paragraph({ text: '', pageBreakBefore: true })
  );

  // TOC
  children.push(
    new Paragraph({ text: 'Table of Contents', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { before: 400, after: 600 } })
  );

  chapters.sort((a, b) => a.chapter_number - b.chapter_number).forEach((chapter) => {
    children.push(new Paragraph({ text: `Chapter ${chapter.chapter_number}: ${chapter.title}`, spacing: { after: 200 } }));
  });

  children.push(new Paragraph({ text: '', pageBreakBefore: true }));

  // Chapters
  chapters.sort((a, b) => a.chapter_number - b.chapter_number).forEach((chapter, index) => {
    children.push(
      new Paragraph({ text: `Chapter ${chapter.chapter_number}`, heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { before: 400, after: 200 } }),
      new Paragraph({ text: chapter.title, heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 600 } })
    );

    const content = chapter.polished_content || chapter.content || '';
    content.split('\n\n').filter(p => p.trim()).forEach(paragraph => {
      children.push(new Paragraph({ text: paragraph.trim(), spacing: { after: 300 } }));
    });

    if (index < chapters.length - 1) {
      children.push(new Paragraph({ text: '', pageBreakBefore: true }));
    }
  });

  const doc = new Document({
    sections: [{ properties: { page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } } }, children }]
  });

  const blob = await Packer.toBlob(doc);
  const fileName = `${book.title.replace(/[^a-z0-9]/gi, '_')}.docx`;
  saveAs(blob, fileName);
  
  // Convert to base64
  const arrayBuffer = await blob.arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
  
  return { fileName, base64 };
}

/**
 * Generate ePub book
 */
export async function generateEPubBase64(book, chapters, authorName = '', coverImageUrl = null) {
  const sortedChapters = [...chapters].sort((a, b) => a.chapter_number - b.chapter_number);
  
  // Build ePub content structure
  const epubChapters = sortedChapters.map((chapter) => ({
    title: `Chapter ${chapter.chapter_number}: ${escapeXml(chapter.title)}`,
    data: `<h1>Chapter ${chapter.chapter_number}</h1>
           <h2>${escapeXml(chapter.title)}</h2>
           ${(chapter.polished_content || chapter.content || '')
             .split('\n\n')
             .filter(p => p.trim())
             .map(p => `<p>${escapeXml(p.trim())}</p>`)
             .join('\n')}`
  }));

  // Add about author as final chapter
  epubChapters.push({
    title: 'About the Author',
    data: `<h1>About the Author</h1>
           <p><strong>${escapeXml(authorName || 'Anonymous Author')}</strong></p>
           ${book.description ? `<p><em>${escapeXml(book.description)}</em></p>` : ''}`
  });

  // Create ePub using JSZip (simple ePub structure)
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // mimetype (must be first and uncompressed)
  zip.file('mimetype', 'application/epub+zip', { compression: 'STORE' });

  // META-INF/container.xml
  zip.file('META-INF/container.xml', `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`);

  // Generate chapter files and manifest items
  let manifestItems = '';
  let spineItems = '';
  let navItems = '';

  epubChapters.forEach((ch, i) => {
    const id = `chapter${i + 1}`;
    const filename = `${id}.xhtml`;
    
    zip.file(`OEBPS/${filename}`, `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
<head><title>${ch.title}</title></head>
<body>${ch.data}</body>
</html>`);

    manifestItems += `<item id="${id}" href="${filename}" media-type="application/xhtml+xml"/>\n`;
    spineItems += `<itemref idref="${id}"/>\n`;
    navItems += `<li><a href="${filename}">${ch.title}</a></li>\n`;
  });

  // OEBPS/content.opf (package document)
  zip.file('OEBPS/content.opf', `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="BookId">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="BookId">urn:uuid:${crypto.randomUUID()}</dc:identifier>
    <dc:title>${escapeXml(book.title)}</dc:title>
    <dc:creator>${escapeXml(authorName || 'Anonymous Author')}</dc:creator>
    <dc:language>en</dc:language>
    <meta property="dcterms:modified">${new Date().toISOString().split('.')[0]}Z</meta>
  </metadata>
  <manifest>
    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
    ${manifestItems}
  </manifest>
  <spine>
    ${spineItems}
  </spine>
</package>`);

  // OEBPS/nav.xhtml (navigation)
  zip.file('OEBPS/nav.xhtml', `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops">
<head><title>Table of Contents</title></head>
<body>
  <nav epub:type="toc">
    <h1>Table of Contents</h1>
    <ol>${navItems}</ol>
  </nav>
</body>
</html>`);

  // Generate the ePub file
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/epub+zip' });
  const fileName = `${book.title.replace(/[^a-z0-9]/gi, '_')}.epub`;
  saveAs(blob, fileName);

  // Convert to base64
  const arrayBuffer = await blob.arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

  return { fileName, base64 };
}

